import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { isFunction } from 'lodash';
import dayjs from 'dayjs';
import { ReportDateType } from 'wb-reddit/graphql/generated';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { isReddit, DatePickerValue } from '@aleph/shared-components';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';

type CreateRedditReportStore = {
	reportId?: string,
	setReportId: (reportId?: string) => void,
	filters: Record<string, any>,
	setFilters: (filters: ((curr: Record<string, any>) => Record<string, any>) | Record<string, any>) => void,
	breakdowns: string[],
	setBreakdowns: (breakdowns: ((curr: string[]) => string[]) | string[]) => void,
	metrics: string[],
	setMetrics: (metrics: ((curr: string[]) => string[]) | string[]) => void,
	schedule?: string,
	setSchedule: (schedule?: string) => void,
	dates: DatePickerValue | null,
	setDates: (dates: ((curr: DatePickerValue) => DatePickerValue) | DatePickerValue) => void,
	name: string,
	setName: (name: string) => void,
	cancelCreation: () => void,
	time: string,
	setTime: (time: string) => void,
	userIds: string[],
	setUserIds: (userIds: string[]) => void,
	userGroupIds: string[],
	setUserGroupIds: (userGroupIds: string[]) => void,
	privateUserGroupIds: string[],
	setPrivateUserGroupIds: (userGroupIds: string[]) => void,
	loading: boolean,
	setLoading: (loading: boolean) => void,
};

const storage = createJSONStorage<CreateRedditReportStore>(() => localStorage, {
	replacer(key, value) {
		if (key === 'dates') {
			const dates = value as CreateRedditReportStore['dates'];

			if (dates) {
				return {
					from: dates.from?.format(),
					to:   dates.to?.format(),
				};
			}
		}

		return value;
	},
	reviver(key, value) {
		if (key === 'dates') {
			// The type here is whatever is stored in localStorage, in this case return
			// from replacer for dates, see above
			const dates = value as { from: string, to: string };

			return {
				from: dayjs(dates.from),
				to:   dayjs(dates.to),
			};
		}

		return value;
	},
});

const useCreateRedditReport = create(persist<CreateRedditReportStore>((set) => ({
	reportId:    undefined,
	setReportId: reportId => set({ reportId }),
	filters:     {},
	setFilters:  filters => {
		set(curr => ({ filters: isFunction(filters) ? filters(curr.filters) : filters }));
	},
	breakdowns:    [],
	setBreakdowns: breakdowns => {
		set(curr => ({ breakdowns: isFunction(breakdowns) ? breakdowns(curr.breakdowns) : breakdowns }));
	},
	metrics:    [],
	setMetrics: metrics => {
		set(curr => ({ metrics: isFunction(metrics) ? metrics(curr.metrics) : metrics }));
	},
	schedule:    isReddit() ? ReportDateType.Custom : SpotifyReportSchedule.OnlyOnce,
	setSchedule: schedule => set({ schedule }),
	dates:       null,
	setDates:    dates => {
		set(curr => ({ dates: isFunction(dates) ? dates(curr.dates) : dates }));
	},
	name:    'New Report',
	setName: name => set({ name }),
	time:    '12:00',
	setTime: time => {
		if (time !== '') {
			const hour = time?.split?.(':')?.[0] ?? 0;
			const minutes = Number(time?.split?.(':')?.[1] ?? 0);
			const roundedMins = (Math.round(minutes / 15) * 15) % 60;

			set({ time: `${hour}:${roundedMins || '00'}` });
		}
	},
	userIds:                [],
	setUserIds:             userIds => set({ userIds }),
	userGroupIds:           [],
	setUserGroupIds:        userGroupIds => set({ userGroupIds }),
	privateUserGroupIds:    [],
	setPrivateUserGroupIds: privateUserGroupIds => set({ privateUserGroupIds }),
	cancelCreation:         () => {
		// TODO - remove this when we have a better way to handle this
		if (useSupergridSettings.getState().lifetimeStats) {
			useSupergridSettings.getState().toggleLifetimeStats();
		}
		set({
			reportId:            undefined,
			filters:             {},
			breakdowns:          [],
			metrics:             [],
			schedule:            isReddit() ? ReportDateType.Custom : SpotifyReportSchedule.OnlyOnce,
			name:                'New Report',
			time: 	              '12:00',
			userIds:             [],
			userGroupIds:        [],
			privateUserGroupIds: [],
			dates:               null,
		});
	},
	loading:    false,
	setLoading: loading => set({ loading }),
}), {
	name: 'create-reddit-report',
	storage,
}));

export default useCreateRedditReport;
